import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { isUnauthenticated } from './api.js';
import { ActivityTab } from './components/ActivityTab.js';
import { BuilderTab } from './components/BuilderTab.js';
import { DeployTab } from './components/DeployTab.js';
import { EnvironmentTab } from './components/EnvironmentTab.js';
import { Layout, PublicLayout } from './components/Layout.js';
import { LogsTab } from './components/LogsTab.js';
import { ErrorNotice, Loading } from './components/Notice.js';
import { OverviewTab } from './components/OverviewTab.js';
import { PeopleTab } from './components/PeopleTab.js';
import { MeContext, useMe } from './hooks.js';
import { AppPage, ServerOnly } from './pages/AppPage.js';
import { AppsPage } from './pages/AppsPage.js';
import { NewAppPage } from './pages/NewAppPage.js';
import { NotInvitedPage } from './pages/NotInvitedPage.js';
import { SignedOutPage } from './pages/SignedOutPage.js';
import { SignInPage } from './pages/SignInPage.js';

/** Everything below needs a signed-in builder. A 401 sends them to sign in and back here afterwards. */
function Protected() {
  const me = useMe();
  const location = useLocation();
  if (me.loading) {
    return (
      <main>
        <Loading />
      </main>
    );
  }
  if (isUnauthenticated(me.error)) {
    const next = location.pathname + location.search;
    return <Navigate to={`/sign-in?next=${encodeURIComponent(next)}`} replace />;
  }
  if (me.error || !me.data) {
    return (
      <main>
        <ErrorNotice error={me.error ?? new Error('Could not load your account.')} />
      </main>
    );
  }
  return (
    <MeContext.Provider value={me.data}>
      <Outlet />
    </MeContext.Provider>
  );
}

export function App() {
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/sign-in" element={<SignInPage />} />
        <Route path="/not-invited" element={<NotInvitedPage />} />
        <Route path="/signed-out" element={<SignedOutPage />} />
      </Route>
      <Route element={<Protected />}>
        <Route element={<Layout />}>
          <Route path="/" element={<AppsPage />} />
          <Route path="/new" element={<NewAppPage />} />
          <Route path="/apps/:slug" element={<AppPage />}>
            <Route index element={<OverviewTab />} />
            <Route path="build" element={<BuilderTab />} />
            <Route path="deploy" element={<DeployTab />} />
            <Route path="people" element={<PeopleTab />} />
            <Route
              path="environment"
              element={
                <ServerOnly>
                  <EnvironmentTab />
                </ServerOnly>
              }
            />
            <Route path="activity" element={<ActivityTab />} />
            <Route
              path="logs"
              element={
                <ServerOnly>
                  <LogsTab />
                </ServerOnly>
              }
            />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}

function NotFound() {
  return (
    <div className="card centered">
      <h1>Page not found</h1>
      <p>
        <a href="/">Back to your apps</a>
      </p>
    </div>
  );
}
